import React from "react";
import { useTranslation } from "react-i18next";
import { FullDivider } from "./SubPage";

type Section = {
  number: string;
  title: string;
  content: string;
  list?: string[];
  footer?: string;
  rights?: [string, string][];
  rightPrefix?: string;
};

function TOCLink({ number, label }: { number: string; label: string }) {
  return (
    <a
      href={`#ds-${number}`}
      onClick={(e) => {
        e.preventDefault();
        document.getElementById(`ds-${number}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }}
      className="flex items-center gap-3 text-[12px] text-grey-500 hover:text-white/70 transition-colors py-1"
    >
      <span className="font-mono text-[10px] text-grey-600 w-4 text-right">{number}</span>
      <span>{label}</span>
    </a>
  );
}

export function Datenschutz() {
  const { t } = useTranslation("datenschutz");
  const sections = t("sections", { returnObjects: true }) as Section[];

  return (
    <div className="pt-14 bg-grey-975">
      <div className="px-5 sm:px-8 md:px-[66px] py-16 md:py-24">
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white tracking-tight mb-6 max-w-2xl leading-none">
          {t("title")}
        </h1>
        <p className="text-[12px] tracking-[0.01em] text-grey-500">{t("lastUpdated")}</p>
      </div>
      <FullDivider />

      <div className="px-5 sm:px-8 md:px-[66px] py-8 md:py-10">
        <p className="text-[12px] tracking-[0.01em] text-grey-500 mb-3">{t("toc")}</p>
        <div className="space-y-0.5">
          {sections.map((s) => (
            <TOCLink key={s.number} number={s.number} label={s.title} />
          ))}
        </div>
      </div>
      <FullDivider />

      <div>
        {sections.map((section) => (
          <div key={section.number} id={`ds-${section.number}`} className="px-5 sm:px-8 md:px-[66px] py-8 md:py-10">
            <div className="flex gap-4 sm:gap-6">
              <div className="shrink-0 flex flex-col items-center">
                <span className="text-[11px] font-mono text-grey-600 mb-2">
                  {section.number.padStart(2, "0")}
                </span>
                <div className="flex-1 w-px bg-white/[0.06]" />
              </div>
              <div>
                <h2 className="text-[15px] font-medium text-white mb-3">{section.title}</h2>
                <div className="text-[13px] text-grey-400 leading-[1.75] space-y-3">
                  <p>{section.content}</p>
                  {section.list && (
                    <ul className="space-y-1.5 ml-1">
                      {section.list.map((item) => (
                        <li key={item} className="flex items-start gap-2.5">
                          <span className="shrink-0 w-1 h-1 rounded-full bg-white/15 mt-[9px]" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.footer && <p>{section.footer}</p>}
                  {section.rights && (
                    <ul className="space-y-1.5 ml-1">
                      {section.rights.map(([right, ref]) => (
                        <li key={ref} className="flex items-start gap-2.5">
                          <span className="shrink-0 w-1 h-1 rounded-full bg-white/15 mt-[9px]" />
                          <span>
                            {section.rightPrefix} {right}{" "}
                            <span className="text-grey-500 font-mono text-[11px]">({ref})</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <FullDivider />
    </div>
  );
}
